import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Dialog, DialogPanel } from "@tremor/react";
import HarViewer from "../HarViewer/index.jsx";
import FindingCard from "./FindingCard.jsx";
import client from "../../api/client.js";

const STATUS_STYLES = {
  up: "bg-green-100 text-green-700 border-green-200",
  degraded: "bg-amber-100 text-amber-700 border-amber-200",
  down: "bg-red-100 text-red-700 border-red-200",
};

function StatusBadge({ status }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600 border-gray-200",
      ].join(" ")}
    >
      {status ?? "unknown"}
    </span>
  );
}

function MetricRow({ label, value, unit, warn, bad }) {
  const numVal = typeof value === "number" ? value : null;
  const color =
    numVal !== null && bad !== undefined && numVal >= bad
      ? "text-red-600"
      : numVal !== null && warn !== undefined && numVal >= warn
        ? "text-amber-600"
        : "text-gray-900";
  return (
    <div className="flex items-center justify-between py-1.5 text-sm border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${color}`}>
        {numVal !== null ? `${Math.round(numVal)}${unit ?? ""}` : value ?? "—"}
      </span>
    </div>
  );
}

export default function WordPressCheckDrawer({ check, onClose }) {
  const { t } = useTranslation("monitor_detail");
  const meta = check.data ?? {};
  const perf = meta.performance ?? {};
  const wp = meta.wordpress ?? {};
  const findings = meta.findings ?? [];
  const screenshots = meta.screenshots ?? [];

  const [activeTab, setActiveTab] = useState("overview");
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [har, setHar] = useState(null);
  const [harLoading, setHarLoading] = useState(false);
  const [harError, setHarError] = useState(null);

  const brokenFindings = useMemo(
    () => findings.filter((f) => f.severity === "broken"),
    [findings]
  );
  const noticeFindings = useMemo(
    () => findings.filter((f) => f.severity === "notice"),
    [findings]
  );

  useEffect(() => {
    if (!meta.har_url) return;
    setHarLoading(true);
    client
      .get(meta.har_url)
      .then((res) => {
        const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        setHar(data);
      })
      .catch(() => setHarError(t("wordpress_drawer.error_har")))
      .finally(() => setHarLoading(false));
  }, [meta.har_url, t]);

  useEffect(() => {
    setActiveScreenshot(0);
  }, [check.time, screenshots.length]);

  const tabs = [
    { id: "overview",    label: t("wordpress_drawer.tab_overview") },
    { id: "vitals",      label: t("wordpress_drawer.tab_vitals") },
    findings.length > 0 && {
      id: "findings",
      label: t("wordpress_drawer.tab_findings", { count: findings.length }),
    },
    (wp.version || wp.theme || wp.plugins_detected?.length) && {
      id: "fingerprint",
      label: t("wordpress_drawer.tab_fingerprint"),
    },
    screenshots.length > 0 && {
      id: "screenshots",
      label: t("wordpress_drawer.tab_screenshots", { count: screenshots.length }),
    },
    meta.har_url && { id: "har", label: t("wordpress_drawer.tab_har") },
  ].filter(Boolean);

  const vitals = [
    { key: "lcp_ms",  label: t("wordpress_drawer.vitals_lcp_label"),  unit: "ms", good: 2500, poor: 4000, desc: t("wordpress_drawer.vitals_lcp_desc") },
    { key: "fcp_ms",  label: t("wordpress_drawer.vitals_fcp_label"),  unit: "ms", good: 1800, poor: 3000, desc: t("wordpress_drawer.vitals_fcp_desc") },
    { key: "ttfb_ms", label: t("wordpress_drawer.vitals_ttfb_label"), unit: "ms", good: 800,  poor: 1800, desc: t("wordpress_drawer.vitals_ttfb_desc") },
    { key: "cls",     label: t("wordpress_drawer.vitals_cls_label"),  unit: "",   good: 0.1,  poor: 0.25, desc: t("wordpress_drawer.vitals_cls_desc") },
  ];

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-2xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t("wordpress_drawer.title")}</h2>
            <p className="mt-0.5 text-xs text-gray-400">{new Date(check.time).toLocaleString()}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-100 px-6 py-3 shrink-0 flex items-start gap-3 flex-wrap">
          <StatusBadge status={check.status} />
          {wp.failure_mode && (
            <span className="text-xs text-red-600 font-medium">{wp.failure_mode.replace(/_/g, " ")}</span>
          )}
          {meta.caching?.plugins?.length > 0 && (
            <span className="text-xs text-gray-400">{t("wordpress_drawer.cache_label")} {meta.caching.plugins.join(", ")}</span>
          )}
          {meta.caching?.cdn && (
            <span className="text-xs text-gray-400">{t("wordpress_drawer.cdn_label")} {meta.caching.cdn}</span>
          )}
          {check.error && !wp.failure_mode && (
            <span className="text-xs text-gray-600">{check.error}</span>
          )}
          {brokenFindings.length > 0 && (
            <div className="w-full flex flex-wrap gap-2 pt-1">
              {brokenFindings.map((f) => (
                <span
                  key={f.id}
                  className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700"
                >
                  {f.title}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-1 border-b border-gray-200 px-6 shrink-0">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveTab(item.id)}
              className={[
                "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                activeTab === item.id
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "overview" && (
            <div className="space-y-5">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("wordpress_drawer.section_performance")}
                </p>
                <MetricRow label={t("wordpress_drawer.metric_ttfb")} value={perf.ttfb_ms} unit=" ms" warn={800} bad={1800} />
                <MetricRow label={t("wordpress_drawer.metric_fcp")} value={perf.fcp_ms} unit=" ms" warn={1800} bad={3000} />
                <MetricRow label={t("wordpress_drawer.metric_lcp")} value={perf.lcp_ms} unit=" ms" warn={2500} bad={4000} />
                <MetricRow label={t("wordpress_drawer.metric_cls")} value={perf.cls != null ? perf.cls.toFixed(3) : null} warn={0.1} bad={0.25} />
                <MetricRow label={t("wordpress_drawer.metric_dom_loaded")} value={perf.dom_loaded_ms} unit=" ms" />
                <MetricRow label={t("wordpress_drawer.metric_page_load")} value={perf.load_ms} unit=" ms" />
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t("wordpress_drawer.section_rest_api")}
                </p>
                {wp.rest_api_status != null ? (
                  <>
                    <MetricRow label={t("wordpress_drawer.metric_status")} value={wp.rest_api_status} />
                    <MetricRow label={t("wordpress_drawer.metric_functional")} value={wp.rest_api_functional ? t("wordpress_drawer.yes") : t("wordpress_drawer.no")} />
                    {wp.rest_api_namespaces?.length > 0 && (
                      <MetricRow label={t("wordpress_drawer.metric_namespaces")} value={wp.rest_api_namespaces.slice(0, 5).join(", ")} />
                    )}
                  </>
                ) : (
                  <p className="text-xs text-gray-400">{t("wordpress_drawer.not_checked")}</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "vitals" && (
            <div className="space-y-4">
              {vitals.map(({ key, label, unit, good, poor, desc }) => {
                const val = perf[key];
                const num = typeof val === "number" ? val : null;
                const rating =
                  num === null ? null
                  : num <= good ? t("wordpress_drawer.rating_good")
                  : num <= poor ? t("wordpress_drawer.rating_needs_improvement")
                  : t("wordpress_drawer.rating_poor");
                const ratingColor =
                  rating === t("wordpress_drawer.rating_good") ? "text-green-600"
                  : rating === t("wordpress_drawer.rating_poor") ? "text-red-600"
                  : "text-amber-600";
                return (
                  <div key={key} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{label}</p>
                        <p className="mt-0.5 text-xs text-gray-400">{desc}</p>
                      </div>
                      {rating && <span className={`text-xs font-medium ${ratingColor}`}>{rating}</span>}
                    </div>
                    <p className="mt-3 text-2xl font-bold text-gray-900">
                      {num !== null ? `${key === "cls" ? val.toFixed(3) : Math.round(num)}${unit}` : "—"}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {t("wordpress_drawer.rating_range", { good, poor, unit })}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "findings" && (
            <div className="space-y-6">
              {brokenFindings.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
                    {t("wordpress_drawer.findings_broken_heading", {
                      count: brokenFindings.length,
                    })}
                  </p>
                  {brokenFindings.map((f) => (
                    <FindingCard key={f.id} finding={f} />
                  ))}
                </div>
              )}

              {noticeFindings.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                    {t("wordpress_drawer.findings_notice_heading", {
                      count: noticeFindings.length,
                    })}
                  </p>
                  {noticeFindings.map((f) => (
                    <FindingCard key={f.id} finding={f} />
                  ))}
                </div>
              )}

              {findings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <p className="text-sm font-medium text-gray-600">
                    {t("wordpress_drawer.findings_none")}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "fingerprint" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{t("wordpress_drawer.section_wordpress")}</p>
                <MetricRow label={t("wordpress_drawer.metric_version")} value={wp.version ?? t("wordpress_drawer.unknown")} />
                <MetricRow label={t("wordpress_drawer.metric_theme")} value={wp.theme ?? t("wordpress_drawer.unknown")} />
                {wp.woocommerce && <MetricRow label={t("wordpress_drawer.metric_woocommerce")} value={t("wordpress_drawer.detected")} />}
                {wp.page_builders?.length > 0 && (
                  <MetricRow label={t("wordpress_drawer.metric_page_builders")} value={wp.page_builders.join(", ")} />
                )}
                {wp.seo_plugins?.length > 0 && <MetricRow label={t("wordpress_drawer.metric_seo_plugins")} value={wp.seo_plugins.join(", ")} />}
                {wp.security_plugins?.length > 0 && <MetricRow label={t("wordpress_drawer.metric_security_plugins")} value={wp.security_plugins.join(", ")} />}
              </div>

              {wp.plugins_detected?.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("wordpress_drawer.detected_plugins", { count: wp.plugins_detected.length })}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {wp.plugins_detected.map((plugin) => (
                      <span
                        key={plugin}
                        className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600"
                      >
                        {plugin}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "screenshots" && screenshots.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {screenshots.map((shot, index) => (
                  <button
                    key={`${shot.label}-${index}`}
                    type="button"
                    onClick={() => setActiveScreenshot(index)}
                    className={[
                      "rounded-full border px-3 py-1 text-xs font-medium",
                      activeScreenshot === index
                        ? "border-teal-600 bg-teal-50 text-teal-600"
                        : "border-gray-200 bg-white text-gray-500",
                    ].join(" ")}
                  >
                    {shot.label ?? t("wordpress_drawer.screenshot_fallback", { num: index + 1 })}
                  </button>
                ))}
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-gray-900 capitalize">
                    {screenshots[activeScreenshot]?.label ?? t("wordpress_drawer.screenshot_label")}
                  </p>
                  {screenshots[activeScreenshot]?.note && (
                    <p className="mt-1 text-xs text-gray-400">{screenshots[activeScreenshot].note}</p>
                  )}
                </div>
                {screenshots[activeScreenshot]?.url && (
                  <img
                    src={screenshots[activeScreenshot].url}
                    alt={screenshots[activeScreenshot].label ?? t("wordpress_drawer.img_alt")}
                    className="w-full rounded border border-gray-200"
                  />
                )}
              </div>
            </div>
          )}

          {activeTab === "har" && meta.har_url && (
            <>
              {harLoading && <p className="text-xs text-gray-400">{t("wordpress_drawer.loading_har")}</p>}
              {harError && <p className="text-xs text-red-500">{harError}</p>}
              {har && <HarViewer har={har} />}
            </>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}
